import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { FormRow, Alert, Button } from 'components/common';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Controller, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { FaInfoCircle, FaSave } from 'react-icons/fa';
import {
  MdArrowBack, MdExpandMore, MdExpandLess,
  MdWork, MdPeopleAlt, MdCategory, MdManageAccounts, MdNotes,
  MdEngineering, MdSupportAgent, MdApartment, MdBusiness,
  MdPersonOutline, MdGroupWork,
} from 'react-icons/md';
import AsyncSelect from 'react-select/async';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import PropTypes from 'prop-types';

import { debounce } from 'helpers/debounce';
import { objectSerializer, adjustToNoon } from 'helpers/utils';
import { INPUT_CLS, TEXTAREA_CLS, SELECT_STYLES } from 'helpers/formStyles';
import apiAxios from 'helpers/apiAxios';

import { getActiveSalesUnits } from 'redux/crm/salesUnitSlice';
import { getBuildingTypeLevel1, getBuildingTypeLevel2, getContactModes, getLeadSources } from 'redux/crm/settingSlice';
import { getCustomerContacts } from 'redux/crm/customerSlice';
import { createProject, updateProject } from 'redux/crm/projectSlice';
import { getAllCompetitors } from 'redux/crm/competiorSlice';

import EmployeeSelectField from 'components/common/select/EmployeeSelectField';
import GoogleMapAutoCompletePlaceDebounce from 'components/common/google-map-places/GoogleMapAutoCompletePlaceDebounce';
import CompetitorForm from '../competitor/CompetitorForm';
import CompetiorList from '../competitor/CompetitorList';
import CustomerFormModal from './detail/CustomerFormModal';
import ContactFormModal from './detail/ContactFormModal';
// ── Employee loader with deduplication cache ─────────────────────────────────
const _salesEmpCache = new Map();
const loadSalesEmployees = (inputValue) => {
  const key = inputValue || '';
  if (!_salesEmpCache.has(key)) {
    const p = apiAxios
      .get(`/organization/employees/public_list?employee_type=sales&page=1&per_page=30&q=${encodeURIComponent(key)}`)
      .then(({ data }) =>
        (Array.isArray(data) ? data : []).map(item => {
          const a = item.attributes || item;
          return { value: a.name, label: a.name, email: a.email || null, fullName: a.full_name || null };
        })
      );
    _salesEmpCache.set(key, p);
    setTimeout(() => _salesEmpCache.delete(key), 300000);
  }
  return _salesEmpCache.get(key);
};

// ── Section configs ───────────────────────────────────────────────────────────
const SECTION_CONFIG = {
  info:        { badge: 1, label: 'Project Information', icon: MdWork,           accent: '#4f46e5' },
  customer:    { badge: 2, label: 'Customer Information', icon: MdPeopleAlt,     accent: '#0891b2' },
  segment:     { badge: 3, label: 'Segmentation',         icon: MdCategory,      accent: '#7c3aed' },
  responsible: { badge: 4, label: 'Responsible',          icon: MdManageAccounts,accent: '#059669' },
  notes:       { badge: 5, label: 'Notes',                icon: MdNotes,         accent: '#d97706' },
};

// ── Collapsible section ───────────────────────────────────────────────────────
const Section = ({ sectionKey, open, onToggle, children }) => {
  const { badge, label, icon: Icon, accent } = SECTION_CONFIG[sectionKey];
  return (
    <div className="tw-rounded-lg tw-overflow-hidden tw-mb-3 tw-shadow-sm"
      style={{ border: '1px solid #e2e8f0', borderLeft: `3px solid ${accent}` }}>
      <button
        type="button"
        onClick={onToggle}
        className="tw-w-full tw-flex tw-items-center tw-gap-3 tw-px-4 tw-py-3 tw-transition-colors tw-border-0 tw-cursor-pointer tw-text-left hover:tw-bg-slate-50 tw-bg-white"
      >
        <div className="tw-flex tw-items-center tw-justify-center tw-w-6 tw-h-6 tw-rounded-xl tw-text-[11px] tw-font-bold tw-text-white tw-shrink-0"
          style={{ background: accent }}>
          {badge}
        </div>
        <Icon size={15} style={{ color: accent }} className="tw-shrink-0" />
        <span className="tw-text-sm tw-font-semibold tw-flex-1 tw-text-slate-800">{label}</span>
        {open
          ? <MdExpandLess size={18} className="tw-text-slate-400" />
          : <MdExpandMore size={18} className="tw-text-slate-400" />
        }
      </button>
      {open && (
        <div className="tw-px-5 tw-py-4 tw-flex tw-flex-col tw-border-t tw-border-slate-100 tw-bg-white">
          {children}
        </div>
      )}
    </div>
  );
};
Section.propTypes = {
  sectionKey: PropTypes.string.isRequired,
  open:       PropTypes.bool.isRequired,
  onToggle:   PropTypes.func.isRequired,
  children:   PropTypes.node.isRequired,
};

// ── Responsibility card ───────────────────────────────────────────────────────
const RespCard = ({ icon: Icon, title, accent, children }) => (
  <div className="tw-rounded-lg tw-overflow-hidden tw-border tw-border-slate-100 tw-shadow-sm">
    <div className="tw-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2.5"
      style={{ background: `${accent}12`, borderBottom: `1px solid ${accent}22` }}>
      <div className="tw-rounded-md tw-p-1" style={{ background: `${accent}22` }}>
        <Icon size={12} style={{ color: accent }} />
      </div>
      <span className="tw-text-xs tw-font-bold" style={{ color: accent }}>{title}</span>
    </div>
    <div className="tw-px-4 tw-py-3 tw-flex tw-flex-col tw-bg-white">{children}</div>
  </div>
);
RespCard.propTypes = {
  icon:     PropTypes.elementType.isRequired,
  title:    PropTypes.string.isRequired,
  accent:   PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
};

// ── Customer sub-tabs ─────────────────────────────────────────────────────────
const CUSTOMER_TABS = [
  { key: 'enduser',    label: 'End-user',   icon: MdPersonOutline },
  { key: 'contractor', label: 'Contractor', icon: MdBusiness },
  { key: 'consultant', label: 'Consultant', icon: MdEngineering },
  { key: 'competitor', label: 'Competitor', icon: MdGroupWork },
];

// ── Inline icon button ────────────────────────────────────────────────────────
const IconBtn = ({ title, onClick, children }) => (
  <button type="button" title={title} onClick={onClick}
    className="tw-inline-flex tw-items-center tw-justify-center tw-w-8 tw-h-8 tw-rounded tw-text-sm tw-font-semibold tw-text-slate-500 hover:tw-bg-slate-100 hover:tw-text-primary-600 tw-bg-white tw-border tw-border-slate-200 tw-cursor-pointer tw-transition-colors tw-shrink-0 tw-leading-none">
    {children}
  </button>
);
IconBtn.propTypes = { title: PropTypes.string, onClick: PropTypes.func, children: PropTypes.node };

// ── Main component ────────────────────────────────────────────────────────────
const ProjectForm = ({ title, project, onSuccess, onClose }) => {
  const { control, register, handleSubmit, formState: { errors }, setValue, getValues, reset, setError } = useForm();
  const { activeSalesUnitsData }                                               = useSelector(state => state.crm_sales_unit);
  const { data: competitorData }                                               = useSelector(state => state.crm_competitor);
  const { buildingTyleLevel1Data, buildingTyleLevel2Data, leadSourceData, contactModeData } = useSelector(state => state.crm_setting);
  const { sales_unit }                                                         = useSelector(state => state.profile_user?.data);

  const [contactStates, setContactStates] = useState({ customers: null, endUsers: null, contractors: null, consultants: null });
  const [contactKeys,   setContactKeys]   = useState({ customer: 0, endUser: 0, contractor: 0, consultant: 0 });
  const [modals,        setModals]        = useState({ competitor: false, competitorList: false, customer: false, contact: false });
  const [address,       setAddress]       = useState(project?.pj_location || null);
  const [currentCustomerId, setCurrentCustomerId] = useState(null);
  const [, setAllowCreateContact] = useState(false);
  const [sections,    setSections]        = useState({ info: true, customer: true, segment: true, responsible: true, notes: true });
  const [customerTab, setCustomerTab]     = useState('enduser');

  const dispatch  = useDispatch();
  const navigate  = useNavigate();
  const disabledSalesUnitOption = sales_unit?.id !== null;

  useEffect(() => {
    if (title === 'Create new PCV') reset();
  }, [title, reset]);

  useEffect(() => {
    dispatch(getActiveSalesUnits());
    dispatch(getBuildingTypeLevel1());
    dispatch(getLeadSources());
    dispatch(getContactModes());
    if (competitorData.length === 0) dispatch(getAllCompetitors());
  }, [dispatch, competitorData.length]);

  // Defined before the effects below so the project-load effect can list it as a dependency.
  const handleSelectChange = useCallback(async (selectedValue, customerKey, stateKey, contactKey, keyType, { resetContact = true } = {}) => {
    if (!selectedValue) { setValue(customerKey, null); return; }
    setValue(customerKey, selectedValue);
    const response = await dispatch(getCustomerContacts(selectedValue));
    if (response.payload) setContactStates(prev => ({ ...prev, [stateKey]: objectSerializer(response.payload) }));
    if (resetContact) setValue(contactKey, null);
    setContactKeys(prev => ({ ...prev, [keyType]: prev[keyType] + 1 }));
  }, [dispatch, setValue]);

  useEffect(() => {
    if (!project) return;
    // Load contact options only — keep the saved contact (a locked contact select can't be re-picked).
    const keepContact = { resetContact: false };
    if (project.company_id) handleSelectChange(project.company_id, 'company_id', 'customers', 'contact_id', 'customer', keepContact);
    if (project.enduser_id) handleSelectChange(project.enduser_id, 'enduser_id', 'endUsers', 'enduser_contact_id', 'endUser', keepContact);
  }, [project, handleSelectChange]);

  useEffect(() => {
    setValue('sales_unit_id', sales_unit?.id);
    if (project) {
      setCurrentCustomerId(project.customer?.id);
      Object.entries(project).forEach(([key, value]) => {
        if (key === 'expected_date_of_signing_contract') {
          const parsed = Date.parse(value);
          if (!isNaN(parsed)) setValue(key, new Date(parsed));
        } else {
          setValue(key, value);
        }
      });
    }
  }, [setValue, project, sales_unit]);

  useEffect(() => {
    if (project?.contact_id && contactStates.customers?.length > 0) {
      if (contactStates.customers.some(c => c.id === project.contact_id)) {
        setValue('contact_id', project.contact_id, { shouldValidate: true });
      }
    }
  }, [contactStates.customers, project, setValue]);

  const fetchBuildingTypeLevel2 = useCallback((id) => dispatch(getBuildingTypeLevel2({ parent_id: id })), [dispatch]);

  // ── Memoized options ──
  const salesUnitOptions      = useMemo(() => (activeSalesUnitsData || []).map(o => ({ value: o.id, label: `${o.name} - ${o.description}` })), [activeSalesUnitsData]);
  const buildingTypeL1Options = useMemo(() => (buildingTyleLevel1Data || []).map(o => ({ value: o.id, label: o.name })), [buildingTyleLevel1Data]);
  const buildingTypeL2Options = useMemo(() => (buildingTyleLevel2Data || []).map(o => ({ value: o.id, label: o.name })), [buildingTyleLevel2Data]);
  const leadSourceOptions     = useMemo(() => (leadSourceData || []).map(o => ({ value: o.name, label: o.name })), [leadSourceData]);
  const contactModeOptions    = useMemo(() => (contactModeData || []).map(o => ({ value: o.name, label: o.name })), [contactModeData]);
  const competitorOptions     = useMemo(() => (competitorData || []).map(o => ({ value: o.id, label: o.name })), [competitorData]);

  const createDefaultOption   = useCallback((obj, key, labelKey = 'name') => obj?.[key] ? { value: obj[key].id, label: obj[key][labelKey] } : null, []);

  const defaultSalesUnitOption      = useMemo(() => project?.sales_unit_id ? { value: project.sales_unit_id, label: project.sales_unit?.full_name } : { value: sales_unit?.id, label: sales_unit?.full_name }, [project, sales_unit]);
  const defaultBuildingTypeL1Option = useMemo(() => createDefaultOption(project, 'building_type_l1'), [project, createDefaultOption]);
  const defaultBuildingTypeL2Option = useMemo(() => createDefaultOption(project, 'building_type_l2'), [project, createDefaultOption]);
  const defaultLeadSourceOption     = useMemo(() => project?.lead_source ? { value: project.lead_source, label: project.lead_source } : null, [project]);
  const defaultContactModeOption    = useMemo(() => project?.contact_mode ? { value: project.contact_mode, label: project.contact_mode } : null, [project]);
  const defaultCustomerOption       = useMemo(() => createDefaultOption(project, 'customer'), [project, createDefaultOption]);
  const defaultEnduserOption        = useMemo(() => createDefaultOption(project, 'enduser'), [project, createDefaultOption]);
  const defaultContractorOption     = useMemo(() => createDefaultOption(project, 'contractor'), [project, createDefaultOption]);
  const defaultConsultantOption     = useMemo(() => createDefaultOption(project, 'consultant'), [project, createDefaultOption]);
  const defaultCompetitorOption     = useMemo(() => project?.competitor_ids ? competitorOptions.filter(o => project.competitor_ids.includes(o.value)) : null, [project, competitorOptions]);

  const mapContactOptions = useCallback((contacts) => (contacts || []).map(c => ({ value: c.id, label: c.name })), []);

  const contactOptions            = useMemo(() => mapContactOptions(contactStates.customers),  [contactStates.customers, mapContactOptions]);
  const contactEnduserOptions     = useMemo(() => mapContactOptions(contactStates.endUsers),   [contactStates.endUsers, mapContactOptions]);
  const contactContractorOptions  = useMemo(() => mapContactOptions(contactStates.contractors),[contactStates.contractors, mapContactOptions]);
  const contactConsultantOptions  = useMemo(() => mapContactOptions(contactStates.consultants),[contactStates.consultants, mapContactOptions]);

  const getDefaultContactOption = useCallback((proj, contactIdKey, contactKey) =>
    proj?.[contactIdKey] ? { value: proj[contactKey].contact_id, label: proj[contactKey].contact_name } : null
  , []);

  const defaultContactEnduserOption    = useMemo(() => getDefaultContactOption(project, 'enduser_contact_id', 'enduser'), [project, getDefaultContactOption]);
  const defaultContactContractorOption = useMemo(() => getDefaultContactOption(project, 'contractor_contact_id', 'contractor'), [project, getDefaultContactOption]);
  const defaultContactConsultantOption = useMemo(() => getDefaultContactOption(project, 'consultant_contact_id', 'consultant'), [project, getDefaultContactOption]);

  // ── Async loaders ──
  const filterCustomersOptions = useCallback(async (inputValue) => {
    const { data } = await apiAxios.get(`/crm/projects/load_customers?page=1&per_page=30&name_cont=${inputValue}`);
    return (data.rows || []).map(item => ({ value: item.id, label: item.name }));
  }, []);

  const debouncedFilterCustomers = useMemo(() => debounce((inputValue, callback) => {
    filterCustomersOptions(inputValue).then(callback);
  }, 300), [filterCustomersOptions]);

  const customerOptions = useCallback((inputValue, callback) => debouncedFilterCustomers(inputValue, callback), [debouncedFilterCustomers]);

  // ── Handlers ──
  const customerChange   = useCallback(async (val) => { await handleSelectChange(val, 'company_id', 'customers', 'contact_id', 'customer'); setAllowCreateContact(true); setCurrentCustomerId(val); }, [handleSelectChange]);
  const endUserChange    = useCallback(async (val) => handleSelectChange(val, 'enduser_id', 'endUsers', 'enduser_contact_id', 'endUser'), [handleSelectChange]);
  const contractorChange = useCallback(async (val) => handleSelectChange(val, 'contractor_id', 'contractors', 'contractor_contact_id', 'contractor'), [handleSelectChange]);
  const consultantChange = useCallback(async (val) => handleSelectChange(val, 'consultant_id', 'consultants', 'consultant_contact_id', 'consultant'), [handleSelectChange]);

  const handleChangeCompetitor = useCallback((opts) => setValue('competitor_ids', opts.map(o => o.value), { shouldValidate: true }), [setValue]);
  const toggleModal            = useCallback((name, val) => setModals(prev => ({ ...prev, [name]: val })), []);
  const handleRefreshContact   = useCallback(() => handleSelectChange(currentCustomerId, 'company_id', 'customers', 'contact_id', 'customer'), [currentCustomerId, handleSelectChange]);
  const handleBack             = useCallback(() => { if (onClose) onClose(); else navigate(-1); }, [navigate, onClose]);
  const toggleSection          = (key) => setSections(prev => ({ ...prev, [key]: !prev[key] }));

  const validateProjectLocation = useCallback(() => {
    if (!address) { setError('pj_location', { type: 'manual', message: 'Project location must be required' }); return false; }
    return true;
  }, [address, setError]);

  const validateEnduserContractorConsultant = useCallback(() => {
    if (!getValues('enduser_id') && !getValues('contractor_id') && !getValues('consultant_id')) {
      toast.error('Consultant, Contractor or EndUser must be specified.');
      return false;
    }
    return true;
  }, [getValues]);

  const onSubmit = useCallback(async (data) => {
    if (!validateProjectLocation() || !validateEnduserContractorConsultant()) return;
    if (address) {
      data = {
        ...data,
        pj_location: address.description,
        pj_province: address.city_province === undefined ? address.district : address.city_province,
        pj_country:  address.country,
      };
    }

    // The form re-posts every key it was seeded with, so an Opla-linked project would
    // otherwise send its name back on every unrelated edit. The backend drops it too;
    // this keeps the request honest about what it is changing.
    if (project?.permissions?.allow_change_name === false) {
      data = Object.fromEntries(Object.entries(data).filter(([key]) => key !== 'name'));
    }

    try {
      let projectId = '';
      if (project) {
        projectId = project.id;
        await dispatch(updateProject({ projectId, data }));
      } else {
        const response = await dispatch(createProject(data));
        projectId = response.payload.data.id;
      }
      if (projectId) {
        if (onSuccess) onSuccess(projectId);
        else navigate(`/crm/projects/${projectId}`);
      }
    } catch (error) {
      toast.error(error.message);
    }
  }, [address, project, dispatch, navigate, onSuccess, validateProjectLocation, validateEnduserContractorConsultant]);

  const { allow_create_customer, allow_create_contact, allow_change_customer, allow_change_name } = project?.permissions || {};
  // Edit: the name is read-only once the project is linked to Opla — Opla owns it and
  // the portal never pushes a rename back, so anything typed here would be put back by
  // the next opportunity webhook. Create: always editable.
  // Fail open when the flag is absent (stale cached payload) — the backend still enforces it.
  const nameLockedByOpla = Boolean(project) && allow_change_name === false;
  // Edit: customer + contact freeze once a contract is released (admins exempt — backend decides).
  // Create: keep the existing lock-after-first-pick behavior.
  // Fail open when the flag is absent (stale cached payload) — the backend still enforces the lock.
  const customerLockedByContract = Boolean(project) && allow_change_customer === false;
  const customerSelectDisabled = project ? customerLockedByContract : currentCustomerId !== null;

  const renderEmployeeSelect = useCallback((name, label, required = false) => {
    const val = project?.[name];
    return (
      <EmployeeSelectField
        key={val ?? name}
        name={name}
        label={label}
        control={control}
        loadOptions={loadSalesEmployees}
        required={required}
        placeholder="Search employees..."
        defaultValue={val ? { value: val, label: val } : null}
      />
    );
  }, [control, project]);

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>

        {/* ── Page header (hidden when rendered inside a Drawer) ── */}
        {!onClose && (
          <div className="tw-flex tw-items-center tw-justify-between tw-mb-5">
            <div>
              <h1 className="tw-text-lg tw-font-bold tw-text-slate-800 tw-mb-0 tw-leading-tight">{title}</h1>
              <p className="tw-text-xs tw-text-slate-400 tw-mb-0 tw-mt-0.5">
                {project
                  ? `Editing PCV #${project.pcv_number || project.id}`
                  : 'Fill in all required fields to register a new project.'}
              </p>
            </div>
            <Button variant="secondary" size="sm" leftIcon={MdArrowBack} onClick={handleBack} className="tw-shadow-sm">
              Back
            </Button>
          </div>
        )}

        <Alert type="warning" message="Note: All PCV must be submitted to Sales Department." className="tw-mb-4" />

        {/* ── 1. Project information ── */}
        <Section sectionKey="info" open={sections.info} onToggle={() => toggleSection('info')}>
          <FormRow label="Project name" required error={errors.name?.message}>
            <input type="text" placeholder="Enter project name" className={INPUT_CLS} disabled={nameLockedByOpla}
              {...register('name', { required: 'Project name must be required.' })} />
            {nameLockedByOpla && (
              <p className="tw-text-xs tw-text-slate-500 tw-mt-1 tw-mb-0">
                Managed by Opla CRM — rename the opportunity in Opla and it will sync here.
              </p>
            )}
          </FormRow>

          {/* Metrics row */}
          <div className="tw-grid tw-grid-cols-3 tw-gap-3">
            <FormRow label="Amount (USD)" required error={errors.amount?.message}>
              <input type="text" placeholder="0.00" className={INPUT_CLS} disabled={project?.has_job_number}
                {...register('amount', { required: 'Amount must be required.' })} />
            </FormRow>
            <FormRow label="Weight (M.T)" required error={errors.weight?.message}>
              <input type="text" placeholder="0.00" className={INPUT_CLS}
                {...register('weight', { required: 'Weight must be required.' })} />
            </FormRow>
            <FormRow label="Area (m²)" required error={errors.area?.message}>
              <input type="text" placeholder="0.00" className={INPUT_CLS}
                {...register('area', { required: 'Area must be required.' })} />
            </FormRow>
          </div>

          <FormRow label="Project location" required error={errors.pj_location?.message}>
            <GoogleMapAutoCompletePlaceDebounce
              id="project-location"
              setAddress={setAddress}
              defaultAddress={project?.pj_location}
            />
            <p className="tw-flex tw-items-center tw-gap-1 tw-text-[11px] tw-text-slate-400 tw-mt-1 tw-mb-0">
              <FaInfoCircle size={10} />
              Ensure at least the correct province/city and country are provided.
            </p>
          </FormRow>

          <div className="tw-grid tw-grid-cols-2 tw-gap-3">
            <FormRow label="Expected month signing contract">
              <Controller
                control={control}
                name="expected_date_of_signing_contract"
                render={({ field }) => (
                  <DatePicker
                    selected={field.value}
                    onChange={d => field.onChange(adjustToNoon(d))}
                    className={INPUT_CLS}
                    placeholderText="MM-YYYY"
                    dateFormat="MM-yyyy"
                    showMonthYearPicker
                    fixedHeight
                  />
                )}
              />
            </FormRow>

            <FormRow label="Sales unit" required error={errors.sales_unit_id?.message}>
              <Controller
                control={control}
                name="sales_unit_id"
                rules={{ required: 'Sales unit must be required' }}
                render={() => (
                  <Select
                    closeMenuOnSelect
                    options={salesUnitOptions}
                    defaultValue={defaultSalesUnitOption}
                    placeholder="Select..."
                    menuPosition="fixed"
                    styles={SELECT_STYLES}
                    isDisabled={disabledSalesUnitOption}
                    onChange={opt => setValue('sales_unit_id', opt.value, { shouldValidate: true })}
                  />
                )}
              />
            </FormRow>
          </div>

          <FormRow label="Project type">
            <Controller
              control={control}
              name="project_type"
              defaultValue="built_up"
              render={({ field }) => {
                const isHotRoll = field.value === 'hot_roll';
                return (
                  <label className="tw-inline-flex tw-items-center tw-gap-2.5 tw-cursor-pointer tw-select-none">
                    <div
                      className={`tw-relative tw-w-10 tw-h-5 tw-rounded-xl tw-transition-colors tw-cursor-pointer ${isHotRoll ? 'tw-bg-primary-500' : 'tw-bg-slate-300'}`}
                      onClick={() => setValue('project_type', isHotRoll ? 'built_up' : 'hot_roll')}
                    >
                      <div className={`tw-absolute tw-top-0.5 tw-w-4 tw-h-4 tw-bg-white tw-rounded-xl tw-shadow tw-transition-transform ${isHotRoll ? 'tw-translate-x-5' : 'tw-translate-x-0.5'}`} />
                    </div>
                    <span className={`tw-text-sm tw-font-medium tw-transition-colors ${isHotRoll ? 'tw-text-primary-600' : 'tw-text-slate-500'}`}>
                      {isHotRoll ? 'Hot Roll' : 'Built Up'}
                    </span>
                  </label>
                );
              }}
            />
          </FormRow>
        </Section>

        {/* ── 2. Customer information ── */}
        <Section sectionKey="customer" open={sections.customer} onToggle={() => toggleSection('customer')}>
          <FormRow label="Customer name (signed contract with)" required error={errors.company_id?.message}>
            <div className="tw-flex tw-gap-1.5">
              <div className="tw-flex-1">
                <Controller
                  control={control}
                  name="company_id"
                  rules={{ required: 'Customer must be required' }}
                  render={() => (
                    <AsyncSelect
                      cacheOptions defaultOptions
                      loadOptions={customerOptions}
                      defaultValue={defaultCustomerOption}
                      closeMenuOnSelect isClearable
                      placeholder="Search customer..."
                      menuPosition="fixed"
                      styles={SELECT_STYLES}
                      isDisabled={customerSelectDisabled}
                      onChange={(opt) => opt && customerChange(opt.value)}
                    />
                  )}
                />
              </div>
              {allow_create_customer && !customerLockedByContract && (
                <IconBtn title="New customer" onClick={() => toggleModal('customer', true)}>+</IconBtn>
              )}
            </div>
            {customerLockedByContract && project?.has_contract && (
              <p className="tw-text-xs tw-text-slate-500 tw-mt-1 tw-mb-0">
                Locked — contract released. Customer and contact can no longer be changed.
              </p>
            )}
          </FormRow>

          <FormRow label="Contact" required error={errors.contact_id?.message}>
            <div className="tw-flex tw-gap-1.5">
              <div className="tw-flex-1">
                <Controller
                  control={control}
                  name="contact_id"
                  rules={customerLockedByContract ? undefined : { required: 'Contact for the customer must be required' }}
                  render={({ field }) => (
                    <Select
                      closeMenuOnSelect isClearable
                      options={contactOptions}
                      value={contactOptions.find(o => o.value === field.value) || null}
                      placeholder="Select contact..."
                      menuPosition="fixed"
                      styles={SELECT_STYLES}
                      key={contactKeys.customer}
                      isDisabled={customerLockedByContract}
                      onChange={(opt) => field.onChange(opt ? opt.value : null)}
                    />
                  )}
                />
              </div>
              {allow_create_contact && !customerLockedByContract && (
                <>
                  <IconBtn title="New contact" onClick={() => toggleModal('contact', true)}>+</IconBtn>
                  <IconBtn title="Refresh contacts" onClick={handleRefreshContact}>↻</IconBtn>
                </>
              )}
            </div>
          </FormRow>

          {/* Sub-tabs */}
          <div className="tw-mt-2 tw-rounded-lg tw-border tw-border-slate-200 tw-overflow-hidden">
            <div className="tw-flex tw-border-b tw-border-slate-200 tw-bg-slate-50/80">
              {CUSTOMER_TABS.map(tab => {
                const TabIcon = tab.icon;
                const isActive = customerTab === tab.key;
                return (
                  <button
                    key={tab.key}
                    type="button"
                    onClick={() => setCustomerTab(tab.key)}
                    className={`tw-flex-1 tw-flex tw-items-center tw-justify-center tw-gap-1.5 tw-px-3 tw-py-2.5 tw-text-xs tw-font-medium tw-transition-all tw-border-0 tw-cursor-pointer ${
                      isActive
                        ? 'tw-bg-white tw-text-primary-600 tw-shadow-sm'
                        : 'tw-text-slate-500 hover:tw-text-slate-700 tw-bg-transparent'
                    }`}
                    style={isActive ? { borderBottom: '2px solid #4f46e5' } : {}}
                  >
                    <TabIcon size={12} />
                    {tab.label}
                  </button>
                );
              })}
            </div>

            <div className="tw-p-4 tw-bg-white">
              {customerTab === 'enduser' && (
                <>
                  <FormRow label="End user">
                    <div className="tw-flex tw-gap-1.5">
                      <div className="tw-flex-1">
                        <Controller control={control} name="enduser_id" render={() => (
                          <AsyncSelect cacheOptions defaultOptions loadOptions={customerOptions}
                            defaultValue={defaultEnduserOption} closeMenuOnSelect isClearable
                            placeholder="Search end user..." menuPosition="fixed" styles={SELECT_STYLES}
                            onChange={(opt) => endUserChange(opt?.value)}
                          />
                        )} />
                      </div>
                      {allow_create_customer && <IconBtn title="New customer" onClick={() => toggleModal('customer', true)}>+</IconBtn>}
                    </div>
                  </FormRow>
                  <FormRow label="Contact">
                    <Controller control={control} name="enduser_contact_id" render={() => (
                      <Select closeMenuOnSelect isClearable options={contactEnduserOptions}
                        defaultValue={defaultContactEnduserOption} placeholder="Select contact..."
                        menuPosition="fixed" styles={SELECT_STYLES} key={contactKeys.endUser}
                        onChange={opt => setValue('enduser_contact_id', opt ? opt.value : null, { shouldValidate: true })}
                      />
                    )} />
                  </FormRow>
                </>
              )}

              {customerTab === 'contractor' && (
                <>
                  <FormRow label="Contractor">
                    <div className="tw-flex tw-gap-1.5">
                      <div className="tw-flex-1">
                        <Controller control={control} name="contractor_id" render={() => (
                          <AsyncSelect cacheOptions defaultOptions loadOptions={customerOptions}
                            defaultValue={defaultContractorOption} closeMenuOnSelect isClearable
                            placeholder="Search contractor..." menuPosition="fixed" styles={SELECT_STYLES}
                            onChange={(opt) => contractorChange(opt?.value)}
                          />
                        )} />
                      </div>
                      {allow_create_customer && <IconBtn title="New customer" onClick={() => toggleModal('customer', true)}>+</IconBtn>}
                    </div>
                  </FormRow>
                  <FormRow label="Contact">
                    <Controller control={control} name="contractor_contact_id" render={() => (
                      <Select closeMenuOnSelect isClearable options={contactContractorOptions}
                        defaultValue={defaultContactContractorOption} placeholder="Select contact..."
                        menuPosition="fixed" styles={SELECT_STYLES} key={contactKeys.contractor}
                        onChange={opt => setValue('contractor_contact_id', opt ? opt.value : null, { shouldValidate: true })}
                      />
                    )} />
                  </FormRow>
                </>
              )}

              {customerTab === 'consultant' && (
                <>
                  <FormRow label="Consultant">
                    <div className="tw-flex tw-gap-1.5">
                      <div className="tw-flex-1">
                        <Controller control={control} name="consultant_id" render={() => (
                          <AsyncSelect cacheOptions defaultOptions loadOptions={customerOptions}
                            defaultValue={defaultConsultantOption} closeMenuOnSelect isClearable
                            placeholder="Search consultant..." menuPosition="fixed" styles={SELECT_STYLES}
                            onChange={(opt) => consultantChange(opt?.value)}
                          />
                        )} />
                      </div>
                      {allow_create_customer && <IconBtn title="New customer" onClick={() => toggleModal('customer', true)}>+</IconBtn>}
                    </div>
                  </FormRow>
                  <FormRow label="Contact">
                    <Controller control={control} name="consultant_contact_id" render={() => (
                      <Select closeMenuOnSelect isClearable options={contactConsultantOptions}
                        defaultValue={defaultContactConsultantOption} placeholder="Select contact..."
                        menuPosition="fixed" styles={SELECT_STYLES} key={contactKeys.consultant}
                        onChange={opt => setValue('consultant_contact_id', opt ? opt.value : null, { shouldValidate: true })}
                      />
                    )} />
                  </FormRow>
                </>
              )}

              {customerTab === 'competitor' && (
                <FormRow label="Competitor">
                  <Controller control={control} name="competitor_ids" render={() => (
                    <Select closeMenuOnSelect={false} isMulti isClearable
                      options={competitorOptions} defaultValue={defaultCompetitorOption}
                      placeholder="Select competitors..." menuPosition="fixed" styles={SELECT_STYLES}
                      onChange={handleChangeCompetitor}
                    />
                  )} />
                  <div className="tw-flex tw-gap-2 tw-mt-2">
                    <Button variant="secondary" size="sm" onClick={() => toggleModal('competitor', true)}
                      className="tw-text-primary-600 tw-border-primary-200 hover:tw-bg-primary-50">
                      + New Competitor
                    </Button>
                    <Button variant="secondary" size="sm" onClick={() => toggleModal('competitorList', true)}>
                      ☰ View list
                    </Button>
                  </div>
                </FormRow>
              )}
            </div>
          </div>
        </Section>

        {/* ── 3. Segmentation ── */}
        <Section sectionKey="segment" open={sections.segment} onToggle={() => toggleSection('segment')}>
          <div className="tw-grid tw-grid-cols-2 tw-gap-3">
            <FormRow label="Building Type L1" required error={errors.building_type_l1_id?.message}>
              <Controller control={control} name="building_type_l1_id"
                rules={{ required: 'Building type level 1 must be required' }}
                render={() => (
                  <Select closeMenuOnSelect options={buildingTypeL1Options} defaultValue={defaultBuildingTypeL1Option}
                    placeholder="Select..." menuPosition="fixed" styles={SELECT_STYLES}
                    onChange={opt => { setValue('building_type_l1_id', opt.value, { shouldValidate: true }); fetchBuildingTypeLevel2(opt.value); setValue('building_type_l2_id', null); }}
                  />
                )}
              />
            </FormRow>
            <FormRow label="Building Type L2" required error={errors.building_type_l2_id?.message}>
              <Controller control={control} name="building_type_l2_id"
                rules={{ required: 'Building type level 2 must be required' }}
                render={() => (
                  <Select closeMenuOnSelect options={buildingTypeL2Options} defaultValue={defaultBuildingTypeL2Option}
                    placeholder="Select..." menuPosition="fixed" styles={SELECT_STYLES}
                    onChange={opt => setValue('building_type_l2_id', opt.value, { shouldValidate: true })}
                  />
                )}
              />
            </FormRow>
          </div>
          <div className="tw-grid tw-grid-cols-2 tw-gap-3">
            <FormRow label="Lead source" required error={errors.lead_source?.message}>
              <Controller control={control} name="lead_source"
                rules={{ required: 'Lead source must be required' }}
                render={() => (
                  <Select closeMenuOnSelect options={leadSourceOptions} defaultValue={defaultLeadSourceOption}
                    placeholder="Select..." menuPosition="fixed" styles={SELECT_STYLES}
                    onChange={opt => setValue('lead_source', opt.value, { shouldValidate: true })}
                  />
                )}
              />
            </FormRow>
            <FormRow label="How does customer know Pebsteel?" error={errors.contact_mode?.message}>
              <Controller control={control} name="contact_mode"
                //rules={{ required: 'Contact mode must be required' }}
                render={() => (
                  <Select closeMenuOnSelect options={contactModeOptions} defaultValue={defaultContactModeOption}
                    placeholder="Select..." menuPosition="fixed" styles={SELECT_STYLES}
                    onChange={opt => setValue('contact_mode', opt.value, { shouldValidate: true })}
                  />
                )}
              />
            </FormRow>
          </div>
        </Section>

        {/* ── 4. Responsible ── */}
        <Section sectionKey="responsible" open={sections.responsible} onToggle={() => toggleSection('responsible')}>
          <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-3">
            <RespCard icon={MdEngineering} title="Sales Engineer" accent="#4f46e5">
              {renderEmployeeSelect('sales_incharge', 'Incharge', true)}
              {renderEmployeeSelect('sales_manager', 'Manager')}
            </RespCard>
            <RespCard icon={MdSupportAgent} title="Sales Support" accent="#059669">
              {renderEmployeeSelect('sales_support', 'Incharge')}
              {renderEmployeeSelect('sales_support_supervisor', 'Supervisor')}
              {renderEmployeeSelect('sales_support_manager', 'Manager')}
            </RespCard>
            <RespCard icon={MdApartment} title="CSE Area Office" accent="#0891b2">
              {renderEmployeeSelect('cse_area', 'Incharge')}
              {renderEmployeeSelect('csm_area', 'Manager')}
            </RespCard>
            <RespCard icon={MdWork} title="Project Management" accent="#d97706">
              {renderEmployeeSelect('project_coordinator', 'Coordinator')}
              {renderEmployeeSelect('project_manager', 'Manager')}
            </RespCard>
          </div>
        </Section>

        {/* ── 5. Notes ── */}
        <Section sectionKey="notes" open={sections.notes} onToggle={() => toggleSection('notes')}>
          <textarea
            rows={7}
            placeholder="Add any remarks or notes about this project..."
            className={TEXTAREA_CLS}
            {...register('remark')}
          />
        </Section>

        {/* ── Submit bar: sticky inside Drawer, fixed when full-page ── */}
        <div
          className={onClose
            ? 'tw-sticky tw-bottom-0 -tw-mx-6 -tw-mb-5 tw-border-t tw-border-slate-200 tw-px-6 tw-py-3 tw-flex tw-items-center tw-justify-center tw-gap-3'
            : 'tw-fixed tw-bottom-0 tw-left-0 tw-right-0 tw-border-t tw-border-slate-200 tw-px-6 tw-py-3 tw-flex tw-items-center tw-justify-center tw-gap-3 tw-z-50'
          }
          style={{ background: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(8px)' }}
        >
          <Button type="submit" size="sm">
            <FaSave size={12} /> {project ? 'Update PCV' : 'Create PCV'}
          </Button>
          <Button variant="danger" size="sm" onClick={handleBack}>Cancel</Button>
        </div>

      </form>

      {/* Modals */}
      {modals.competitor && (
        <CompetitorForm showModal={modals.competitor} setshowModal={(v) => toggleModal('competitor', v)} title="New Competitor" />
      )}
      {modals.competitorList && (
        <CompetiorList showModal={modals.competitorList} setshowModal={(v) => toggleModal('competitorList', v)} />
      )}
      {modals.contact && (
        <ContactFormModal showModal={modals.contact} setshowModal={(v) => toggleModal('contact', v)} customerId={currentCustomerId} />
      )}
      {modals.customer && (
        <CustomerFormModal showModal={modals.customer} setshowModal={(v) => toggleModal('customer', v)} />
      )}
    </>
  );
};

ProjectForm.propTypes = {
  title:     PropTypes.string.isRequired,
  project:   PropTypes.object,
  onSuccess: PropTypes.func,
  onClose:   PropTypes.func,
};

export default ProjectForm;
